import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { S3Service } from 'src/app/service/s3.service';
import { captureSurfacedFailure } from 'src/app/util/sentry-capture';
import { MessageService } from './message.service';

/** `dynamic-quill-editor` 컴포넌트 인스턴스를 템플릿 ref(`#quill` 등)로 받는다. */
export interface AltEditorHost {
  openAltEditor(img: HTMLImageElement): void;
}

/**
 * 업로드를 허용하는 세로/가로 상한 — 폰 세로 한 화면(≈19.5:9). 넘으면 상세페이지를 통째로 뽑은
 * 초장축이라 alt 한 줄로 설명할 수 없다. 리사이즈 전 원본이라 픽셀이 아닌 비율로 잰다.
 * 콘텐츠 정책이라, 성능 기준인 image.service.ts의 SLICE_MIN_ASPECT_RATIO와는 별개로 움직인다.
 */
const MAX_UPLOAD_ASPECT_RATIO = 2.2;

@Injectable({
  providedIn: 'root',
})
export class FileUploadService {
  constructor(
    private s3Service: S3Service,
    private messageService: MessageService,
  ) {}

  async onFileChange(event, folder) {
    const fileTypes = ['image/gif', 'image/jpeg', 'image/png'];
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      if (fileTypes.find((t) => t === file.type)) {
        if (file.size < 10000000) {
          const res = await this.s3Service.uploadFile(file, folder);
          return res;
        } else {
          this.messageService.popup({
            title: '업로드',
            description: `이미지 사이즈가 10MB를 넘습니다. (사이즈: 약 ${Math.ceil(file.size / 1000)}KB)`,
            cancelLabel: '확인',
          });
        }
      } else {
        this.messageService.popup({
          title: '업로드',
          description: `다음 이미지 형식만 가능합니다. (${fileTypes})`,
          cancelLabel: '확인',
        });
      }
    } else {
      this.messageService.popup({
        title: '업로드',
        description: `파일이 선택되지 않았습니다.`,
        cancelLabel: '확인',
      });
    }
  }

  /** @param path S3 폴더 접두사(`meetings`, `blogs`, `events`). */
  getEditorInstance(editorInstance: any, altEditorHost: AltEditorHost | null, path: string) {
    // Check if toolbar module exists (it won't when showToolbar is false)
    let toolbar = editorInstance.getModule ? editorInstance.getModule('toolbar') : null;

    // Only add image handler if toolbar exists
    if (toolbar && typeof toolbar.addHandler === 'function') {
      toolbar.addHandler('image', () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.click();
        input.onchange = () => {
          const range = editorInstance.getSelection();
          this.fileChanged(editorInstance, range, input.files[0], path, altEditorHost);
        };
      });
    }

    // Always add text-change listener for image handling
    if (editorInstance && editorInstance.on) {
      editorInstance.on('text-change', (delta) => {
        const images = delta.ops
          .filter((op) => op?.insert?.image?.includes('data:image'))
          .map((op) => op?.insert?.image);
        images.forEach((image) => {
          const file = this.base64ToFile(image, 'file');
          const range = editorInstance.getSelection();
          if (range) {
            editorInstance.deleteText(range.index, 1);
            this.fileChanged(editorInstance, range, file, path, altEditorHost);
          }
        });
      });
    }
  }

  /**
   * Quill 인덱스는 임베드 경계 해석에 기대야 해서, 업로드마다 유일한 S3 URL로 직접 매칭한다.
   * src가 겹치면 DOM 상 마지막 노드 — 커서가 앞이면 방금 넣은 게 아닐 수 있으나 같은 파일이다.
   */
  private findInsertedImage(editorInstance: any, src: string): HTMLImageElement | null {
    const root: HTMLElement | undefined = editorInstance?.root;
    if (!root?.querySelectorAll) return null;

    const images = root.querySelectorAll('img');
    for (let i = images.length - 1; i >= 0; i--) {
      if (images[i].getAttribute('src') === src) return images[i];
    }
    return null;
  }

  /** 디코딩에 실패하면 null — 가드를 통과시켜 기존 동작을 유지한다. */
  private readImageSize(file: File): Promise<{ width: number; height: number } | null> {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const probe = new Image();
      probe.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: probe.naturalWidth, height: probe.naturalHeight });
      };
      probe.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      probe.src = url;
    });
  }

  /** 한 화면보다 긴 이미지면 크기와 몇 화면인지를 돌려준다. 아니거나 못 읽으면 null. */
  private async measureIfTallerThanOneScreen(
    file: File,
  ): Promise<{ width: number; height: number; screens: number } | null> {
    const size = await this.readImageSize(file);
    if (!size || size.width === 0) return null;

    const screens = size.height / (size.width * MAX_UPLOAD_ASPECT_RATIO);
    return screens > 1 ? { ...size, screens } : null;
  }

  private async fileChanged(
    editorInstance: any,
    range: any,
    file: File,
    path: string,
    altEditorHost?: AltEditorHost | null,
  ) {
    const fileTypes = ['image/gif', 'image/jpeg', 'image/png', 'image/svg+xml'];

    // 로딩 인디케이터를 넣기 전에 거른다 — 넣고 나서 되돌리면 커서 위치가 흔들린다.
    const tooTall = await this.measureIfTallerThanOneScreen(file);
    if (tooTall) {
      // 반올림이 1로 내려가는 구간(한 화면의 1.0~1.5배)은 "약 1배인데 2장으로 나눠라"가 되어
      // 모순으로 읽힌다 — 그 구간은 배수를 빼고 길다는 사실만 말한다.
      const times = Math.round(tooTall.screens);
      const lengthPhrase = times >= 2 ? `한 화면의 약 ${times}배입니다` : '한 화면보다 깁니다';

      // 나눠 올렸을 때 얻는 것까지 알려준다 — 금지와 단점만 말하면 호스트에게는
      // 손해로만 읽히고, 몇 장으로 나눠야 하는지 모르면 안내가 실행할 수 없는 말이 된다.
      this.messageService.popup({
        title: '업로드',
        description:
          `이 이미지는 ${tooTall.width}×${tooTall.height}로 ${lengthPhrase}. ` +
          `${Math.ceil(tooTall.screens)}장 정도로 나눠서 올려주세요.\n\n` +
          '나눠 올리면 이미지마다 대체 텍스트를 넣을 수 있어 검색에 노출되고, ' +
          '고객이 읽기 편해지며, 화면도 빨리 뜹니다.',
        cancelLabel: '확인',
      });
      return;
    }

    editorInstance.insertEmbed(range.index, 'image', '/assets/loading-dark.gif');
    editorInstance.formatText(range.index, 1, 'width', '25px');

    if (fileTypes.find((t) => t === file.type)) {
      this.s3Service
        .uploadFile(file, path)
        .then((uploadResp) =>
          this.s3Service.resizeImage(uploadResp.key, `${path}-resized`, {
            width: 800,
            fit: 'cover',
          }),
        )
        .then((resizeResp) => {
          const link = resizeResp.url;
          editorInstance.deleteText(range.index, 1);
          editorInstance.insertEmbed(range.index, 'text', `\n\n\n`, 'user');
          editorInstance.insertEmbed(range.index, 'image', `${link}`, 'user');

          // 업로드 직후 alt를 물어본다 — 나중에 채우게 두면 대부분 비어 있는 채로 발행된다.
          const inserted = this.findInsertedImage(editorInstance, link);
          if (!inserted) {
            Sentry.captureMessage('업로드 이미지 DOM 노드를 찾지 못해 alt 편집을 열지 못함', {
              level: 'warning',
              extra: { src: link, path },
            });
            return;
          }
          altEditorHost?.openAltEditor(inserted);
        })
        .catch((error) => {
          // 업로드/리사이즈 실패 시 로딩 인디케이터를 그대로 두면 무한 로딩처럼 보인다.
          // (예: S3 권한 누락으로 인한 AccessDenied) 로딩 이미지를 제거하고 사용자에게
          // 알린 뒤 Sentry로 가시화한다. HTTP 실패는 이미 팝업으로 알렸으므로 warning 으로만.
          editorInstance.deleteText(range.index, 1);
          captureSurfacedFailure(error, { feature: 'editor-image-upload', phase: 'upload-resize', extra: { path } });
          this.messageService.popup({
            title: '업로드',
            description: '이미지 업로드에 실패했어요. 잠시 후 다시 시도해주세요.',
            cancelLabel: '확인',
          });
        });
    } else {
      this.messageService.popup({
        title: '업로드',
        description: `다음 이미지 형식만 가능합니다. (${fileTypes})`,
        cancelLabel: '확인',
      });
      editorInstance.deleteText(range.index, 1);
    }
  }

  base64ToFile(dataurl, fileName) {
    var arr = dataurl.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: mime });
  }

  async urlToFile(url) {
    const fetchUrl = new URL(url, window.location.href);
    // Displayed CDN images may be cached without CORS metadata; fetch a separate URL.
    if (fetchUrl.hostname === 'cdn.nonunbub.com') fetchUrl.searchParams.set('photoEdit', '1');
    const response = await fetch(fetchUrl.toString());
    if (!response.ok) throw new Error(`Photo download failed: HTTP ${response.status}`);
    const data = await response.blob();
    const filename = fetchUrl.protocol === 'data:' ? 'photo' : fetchUrl.pathname.split('/').pop() || 'photo';
    return new File([data], filename, { type: data.type || 'image/jpeg' });
  }
}
